import EmptyStateIllustration from '@/components/EmptyStateIllustration'
import { Box } from '@/components/ui/box'
import { HStack } from '@/components/ui/hstack'
import { IconSymbol } from '@/components/ui/IconSymbol'
import { Image } from '@/components/ui/image'
import { Pressable } from '@/components/ui/pressable'
import { Spinner } from '@/components/ui/spinner'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import useCustomTabBarHeight from '@/lib/hooks/useCustomTabBarHeight'
import useHorizontalSafeAreaPadding from '@/lib/hooks/useHorizontalSafeAreaPadding'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { fetchTradeOrdersThunk, fetchTradeStatsThunk, selectTradeOrdersHasNextPage } from '@/redux/slices/tradeSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import type { TradeOrder } from '@/typings/trade'
import { isRejected } from '@reduxjs/toolkit'
import { useRouter } from 'expo-router'
import { useCallback, useMemo, useState } from 'react'
import { ActivityIndicator, RefreshControl, SectionList, View } from 'react-native'
import { formatDateTime, formatPlainAmount, orderTitle, PAID_STATUS_META } from './tradeFormat'
import TradeOrdersHeader from './TradeOrdersHeader'

export default function TradeOrders() {
  const customTabBarHeight = useCustomTabBarHeight()
  const horizontalPadding = useHorizontalSafeAreaPadding()
  const dispatch = useAppDispatch()
  const toast = useDefaultToast()

  const orders = useAppSelector(state => state.trade.orders)
  const currentPage = useAppSelector(state => state.trade.currentPage)
  const hasNextPage = useAppSelector(selectTradeOrdersHasNextPage)
  const { isLoading, loaded, error } = useAppSelector(state => state.trade.ordersState)

  const [refreshing, setRefreshing] = useState(false)

  // Before the first request starts (user or merchant type still loading) there is nothing to show either
  const initialLoading = !loaded && !error
  const loadingMore = isLoading && hasNextPage

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    const [ordersResult] = await Promise.all([
      dispatch(fetchTradeOrdersThunk({ page: 1 })),
      dispatch(fetchTradeStatsThunk()),
    ])
    setRefreshing(false)
    if (isRejected(ordersResult) && orders.length > 0) toast.show(ordersResult.payload?.message ?? '刷新失败')
  }, [dispatch, orders.length, toast])

  const handleLoadMore = useCallback(async () => {
    if (!hasNextPage || isLoading) return
    const result = await dispatch(fetchTradeOrdersThunk({ page: currentPage + 1 }))
    if (isRejected(result)) toast.show(result.payload?.message ?? '加载失败')
  }, [hasNextPage, isLoading, currentPage, dispatch, toast])

  const sections = useMemo(() => (orders.length > 0 ? [{ data: orders }] : []), [orders])

  if (initialLoading) {
    return (
      <View className="items-center justify-center flex-1">
        <Spinner />
      </View>
    )
  }

  return (
    <SectionList
      sections={sections}
      keyExtractor={item => String(item.id)}
      renderItem={({ item, index }) => <TradeOrderItem order={item} showBorder={index < orders.length - 1} />}
      stickySectionHeadersEnabled={false}
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{ ...horizontalPadding, paddingBottom: customTabBarHeight }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
      onEndReached={handleLoadMore}
      onEndReachedThreshold={0.2}
      ListHeaderComponent={TradeOrdersHeader}
      ListEmptyComponent={
        <View className="items-center justify-center py-16">
          <EmptyStateIllustration message={error ?? '暂无交易订单'} />
        </View>
      }
      ListFooterComponent={
        loadingMore ? (
          <View className="py-4 items-center">
            <ActivityIndicator size="small" />
          </View>
        ) : undefined
      }
    />
  )
}

function TradeOrderItem({ order, showBorder }: { order: TradeOrder; showBorder: boolean }) {
  const router = useRouter()
  const meta = PAID_STATUS_META[order.paidStatus]
  const [statusColor, orangeColor] = usePlatformColor(meta?.color ?? 'secondaryLabel', 'systemOrange')
  const imageUrl = order.items[0]?.goodsImageUrl

  return (
    <Pressable
      onPress={() => router.push(`/trade/orders/${order.id}`)}
      className="data-[active=true]:bg-background-100"
    >
      {/* Same horizontal structure as the form list rows (Cover), so both lists line up */}
      <HStack className="px-2">
        <Box className="p-4">
          {imageUrl ? (
            <Image source={{ uri: imageUrl }} alt="" size="none" className="w-[44px] h-[44px] rounded-[8px]" />
          ) : (
            <View
              className="items-center justify-center bg-secondary-system-background"
              style={{ width: 44, height: 44, borderRadius: 8 }}
            >
              {/* systemGray is the same hex in light and dark mode */}
              <IconSymbol name="bag" size={20} color="#8e8e93" />
            </View>
          )}
        </Box>
        <HStack
          space="md"
          className={`flex-1 items-center py-2 mr-4 ${showBorder ? 'border-b border-background-100' : ''}`}
        >
          <VStack space="sm" className="flex-1">
            <Text size="body" className="text-label" numberOfLines={1}>
              {orderTitle(order.items)}
            </Text>
            <Text size="subhead" className="text-secondary-label">
              {formatDateTime(order.orderedAt)}
            </Text>
          </VStack>
          <VStack space="sm" className="items-end">
            <Text size="body" className="text-label">
              {meta?.sign}
              {formatPlainAmount(order.amount)}
            </Text>
            {order.mockPaid ? (
              <Text size="subhead" style={{ color: orangeColor }}>
                模拟支付
              </Text>
            ) : order.paidStatus !== 'paid' && meta ? (
              <Text size="subhead" style={{ color: statusColor }}>
                {meta.label}
              </Text>
            ) : (
              // Keeps the amount level with the title when there is no status line
              <Text size="subhead"> </Text>
            )}
          </VStack>
        </HStack>
      </HStack>
    </Pressable>
  )
}
